/**
 * The business day's stages (IP-134 as amended on 6 Oct 2026, IP-135; design
 * docs/superpowers/specs/2026-10-06-order-review-business-day-design.md,
 * sections 5.2 and 5.5) - pure: what each stage is called, which of them
 * pause the maker's review, and how far a day may move on the facts its own
 * read finds (business-day/sql.js selectDayFacts). No SQL, no clock: the
 * caller hands the facts and the instant in.
 *
 *   #  stage        entered when
 *   1  OPEN         the row is created (day.js ensureBusinessDay)
 *   2  FROZEN       now >= the day's cut-off (the latest of the funds ACTIVE on
 *                   the date) and no order_book of the date is OPEN
 *   3  PRICED       every fund that NEEDS a price has a current one
 *   4  REPRICING    the Repricing step starts (advance.js)
 *   5  RECONCILING  the Repricing step finished every candidate: the day's
 *                   statements are loaded and matched, late money in the
 *                   funding window is matched, and checkers approve or reject
 *                   the day's orders
 *   6  DAY_END      RECONCILING, the funding window closed, a LOADED statement
 *                   for every ACTIVE COLLECTION account, and nothing pending
 *                   dated the day
 *   7  CLOSED       day end wrote its summary
 *
 * Why reconciliation follows repricing (the tech lead, confirmed by the
 * owner, 6 Oct 2026; migration 0021): D's NAV is published on D's evening and
 * the day is repriced at once, while D's bank statement arrives on D+1's
 * morning and D's book takes money until D+1's cut-off (the funding window).
 * 0019's order - RECONCILING before PRICED, entered on a statement, and a
 * REPRICED stage after the step - is retired: a statement being loaded no
 * longer moves a day. It is the checklist's "statements loaded n/m", whatever
 * the stage.
 *
 * Stages 1 to 3 are pure functions of committed facts (decideStage); FROZEN's
 * facts hold for every stage after it - no stage after OPEN is entered while
 * a book of the date is OPEN. A move goes to the FURTHEST stage the facts
 * reach, so an OPEN day whose price is already in passes over FROZEN.
 *
 * Needing a price (stage 3) is the day's own rule, narrower than the pricing
 * guard (pricing/reads.js pricingGuard): an ACTIVE fund that had units in
 * issue before the date, or has an order dated the date in FROZEN, PRICED,
 * READY or APPROVED. A fund with only a HELD order, or a launch fund with no
 * units yet (ZERO_UNITS), does not hold up the day.
 *
 * DAY_ADVANCE's force (5.5) lets a day past exactly two blocking facts, and
 * says what was missing: funds still without a price (to PRICED), and
 * statements not loaded or the funding window still open (RECONCILING to
 * DAY_END). It never passes pending orders - they are approved or rejected -
 * nor the books: FROZEN's facts are never forced.
 */

/** The stages, by name. */
export const STAGE = Object.freeze({
  OPEN: 'OPEN',
  FROZEN: 'FROZEN',
  PRICED: 'PRICED',
  REPRICING: 'REPRICING',
  RECONCILING: 'RECONCILING',
  DAY_END: 'DAY_END',
  CLOSED: 'CLOSED',
});

/**
 * How long a running step pauses the maker's review (IP-129, design 2.6):
 * the Repricing step for 15 minutes from the instant the day entered it, day
 * end for 5. A step older than its limit pauses nothing - it raises
 * LFOC/<env>/Day StepStale instead - so a stuck step can never stop reviews
 * for long.
 */
// IP-171 (owner, 9 Oct 2026): day end no longer pauses reviews - a review changes no balance, and the Day end
// process's check reads postings dated after its day as later ones. Only the Repricing step pauses them.
export const PAUSE_LIMIT_MS = Object.freeze({ REPRICING: 15 * 60_000 });

const row = (stage, rank, label, column) => Object.freeze({
  stage, rank, label, column, pausesReview: Object.prototype.hasOwnProperty.call(PAUSE_LIMIT_MS, stage),
});

/**
 * Each stage in order (the owner's brief B-4, as amended on 6 Oct 2026:
 * "Frozen > Priced > Repricing > Reconciliation > Day end"), with its rank
 * (migration 0021's business_day_stage_rank), the Console's label and the
 * business_day column that holds the instant the day reached it.
 * RECONCILING's column keeps its 0019 name; 0019's repriced_at is retired
 * (0021: never written again).
 */
export const STAGES = Object.freeze([
  row(STAGE.OPEN, 1, 'Open for transactions', 'opened_at'),
  row(STAGE.FROZEN, 2, 'Cut-off · frozen', 'frozen_at'),
  row(STAGE.PRICED, 3, 'Priced', 'priced_at'),
  // The Console says "Transaction pricing" for this step (owner, 16 Oct 2026): "Priced" (stage 3)
  // means the unit prices are calculated; this step prices the transactions at them. The code,
  // the API and the database keep "repricing".
  row(STAGE.REPRICING, 4, 'Transaction pricing', 'repricing_at'),
  row(STAGE.RECONCILING, 5, 'Reconciliation & approvals', 'reconciling_at'),
  row(STAGE.DAY_END, 6, 'Day end', 'day_end_at'),
  row(STAGE.CLOSED, 7, 'Closed', 'closed_at'),
]);

const BY_STAGE = new Map(STAGES.map((s) => [s.stage, s]));

/**
 * The event reason of a move to each stage (business_day_event.reason): the
 * fact or the step that moved the day there. A force is its own word.
 * RECONCILING is entered as the Repricing step finishes (0019's trail names
 * STATEMENT_LOADED for its RECONCILING, and REPRICING_FINISHED for its
 * REPRICED).
 */
export const MOVE_REASON = Object.freeze({
  OPEN: 'CREATED',
  FROZEN: 'BOOKS_CLOSED',
  PRICED: 'PRICES_PUBLISHED',
  PRICED_FORCED: 'PRICE_FORCED',
  REPRICING: 'REPRICING_STARTED',
  RECONCILING: 'REPRICING_FINISHED',
  DAY_END: 'DAY_END_STARTED',
  DAY_END_FORCED: 'DAY_END_FORCED',
  CLOSED: 'DAY_END_WRITTEN',
});

/** What keeps a day from day end (decideDayEnd's blockedBy). */
export const BLOCKER = Object.freeze({
  PENDING_ORDERS: 'PENDING_ORDERS',
  FUNDING_WINDOW_OPEN: 'FUNDING_WINDOW_OPEN',
  STATEMENTS_MISSING: 'STATEMENTS_MISSING',
});

/** The orders of a date that still have to be dealt (approved or rejected) before its day ends. */
export const PENDING_STATES = Object.freeze(['FROZEN', 'PRICED', 'READY', 'APPROVED']);

/**
 * The subscriptions of a date still waiting for their money - expected, or a
 * cheque in clearing. They hold up nothing (the cut-off rolls them on,
 * books/close.js step 3), but the day shows them.
 */
export const AWAITING_MONEY_STATES = Object.freeze(['AWAITING_FUNDS', 'AWAITING_CLEARANCE']);

/**
 * The steps of advanceBusinessDays (advance.js), as a failure names them -
 * here, browser-safe, so the Console words exactly what the server sends
 * (DAY_ADVANCE's advance.failures[].step).
 */
export const DAY_STEP = Object.freeze({ READ: 'read', TODAY: 'today', SYNC: 'sync', REPRICING: 'repricing', DAY_END: 'dayEnd' });

/** True for one of the seven stage names (REPRICED, retired by 0021, is none). */
export function isStage(value) {
  return typeof value === 'string' && BY_STAGE.has(value);
}

function stageOf(value) {
  const s = BY_STAGE.get(value);
  if (!s) throw new TypeError('not a business day stage: ' + String(value));
  return s;
}

/** A stage's place in the day, 1 (OPEN) to 7 (CLOSED). */
export const rankOf = (stage) => stageOf(stage).rank;

/** A stage's Console label. */
export const labelOf = (stage) => stageOf(stage).label;

/** The business_day column holding the instant a day reached the stage. */
export const columnOf = (stage) => stageOf(stage).column;

/** The stages strictly between two, in order: what a move from `from` to `to` passes over. */
export function stagesBetween(from, to) {
  const low = rankOf(from);
  const high = rankOf(to);
  return STAGES.filter((s) => s.rank > low && s.rank < high).map((s) => s.stage);
}

/** Epoch milliseconds of an instant: a number as it is, a string parsed (RFC 3339 with its offset). */
function msOf(instant, name) {
  const ms = typeof instant === 'number' ? instant : Date.parse(instant);
  if (!Number.isFinite(ms)) throw new TypeError(name + ' is not an instant: ' + String(instant));
  return ms;
}

/**
 * Whether a fund needs a price for the date (design 5.2): ACTIVE on it, and
 * either it had units in issue before the date or an order dated the date is
 * dealt (FROZEN, PRICED, READY or APPROVED - selectDayFacts counts only those,
 * never a HELD or pre-freeze order).
 *
 * @param {{ active: boolean, hasUnits: boolean, dealtOrders: number }} fund
 */
export function needsPrice(fund) {
  return Boolean(fund?.active) && (Boolean(fund.hasUnits) || Number(fund.dealtOrders ?? 0) > 0);
}

/**
 * Whether FROZEN's facts hold: the day's cut-off has passed and none of its
 * books is OPEN. A date with no book at all is frozen at its cut-off: nothing
 * was dealt into it.
 *
 * @param {{ nowMs: number, cutoffAt: string, books: { open: number } }} facts
 */
export function isFrozen(facts) {
  return msOf(facts.nowMs, 'nowMs') >= msOf(facts.cutoffAt, 'cutoffAt') && Number(facts.books?.open ?? 0) === 0;
}

/**
 * The facts of a day, from its own read (sql.js selectDayFacts: one row per
 * fund) - what decideStage, decideDayEnd and the Console's checklist read.
 *
 * The funding window closes at the latest funding_window_closes_at of the
 * date's books (each book's is the next business day's cut-off); with no
 * book, at nextCutoffAt - the next business day's cut-off, which the caller
 * reads from the calendar (null when the calendar ends first: the window is
 * then never seen to close, and only a force passes it).
 *
 * @param {object[]} rows selectDayFacts's rows
 * @param {{ nowMs: number, cutoffAt: string, nextCutoffAt?: string|null }} at
 * @returns {{ nowMs: number, cutoffAt: string, books: { total: number, open: number }, frozen: boolean,
 *   statementLoaded: boolean, funds: object[], fundsMissingPrice: string[], fundingWindowClosesAt: string|null,
 *   statements: { expected: string[], loaded: string[], missing: string[] }, pendingOrders: number }}
 */
export function factsOf(rows, { nowMs, cutoffAt, nextCutoffAt = null }) {
  const iso = (value) => (value === null || value === undefined ? null : new Date(value).toISOString());
  const json = (value) => (typeof value === 'string' ? JSON.parse(value) : (value ?? null));
  const funds = (rows ?? []).map((r) => {
    const accounts = (json(r.accounts) ?? []).map((a) => ({
      accountId: String(a.accountId).toLowerCase(), bankName: a.bankName ?? null, accountNumber: a.accountNumber ?? null, loaded: a.loaded === true,
    }));
    const fund = {
      fundId: r.fund_id,
      active: r.active === true,
      hasUnits: r.has_units === true,
      dealtOrders: Number(r.dealt_orders ?? 0),
      freezableOrders: Number(r.freezable_orders ?? 0),
      awaitingFunds: Number(r.awaiting_funds ?? 0),
      pricePublishedAt: iso(r.price_published_at),
      book: r.book_state ? { state: r.book_state, cutoffAt: iso(r.book_cutoff_at), closedAt: iso(r.book_closed_at) } : null,
      fundingWindowClosesAt: iso(r.funding_window_closes_at),
      navRun: json(r.nav_run),
      accounts,
      statementLoaded: r.statement_loaded === true,
    };
    fund.needsPrice = needsPrice(fund);
    fund.pendingOrders = fund.dealtOrders + fund.freezableOrders;
    return fund;
  });
  const books = funds.filter((f) => f.book);
  const windows = books.map((f) => f.fundingWindowClosesAt).filter(Boolean).sort();
  const accounts = funds.flatMap((f) => f.accounts);
  const facts = {
    nowMs: msOf(nowMs, 'nowMs'),
    cutoffAt: iso(cutoffAt),
    books: { total: books.length, open: books.filter((f) => f.book.state === 'OPEN').length },
    statementLoaded: funds.some((f) => f.statementLoaded),
    funds,
    fundsMissingPrice: funds.filter((f) => f.needsPrice && !f.pricePublishedAt).map((f) => f.fundId),
    fundingWindowClosesAt: windows.length ? windows[windows.length - 1] : iso(nextCutoffAt),
    statements: {
      expected: accounts.map((a) => a.accountId),
      loaded: accounts.filter((a) => a.loaded).map((a) => a.accountId),
      missing: accounts.filter((a) => !a.loaded).map((a) => a.accountId),
    },
    pendingOrders: funds.reduce((n, f) => n + f.pendingOrders, 0),
  };
  facts.frozen = isFrozen(facts);
  return facts;
}

/**
 * Stages 1 to 3 (design 5.2): the furthest of them the facts reach, as a
 * move from the day's current stage - or null when the facts take it no
 * further. A day already at PRICED or beyond is the steps' (advance.js), so
 * sync never moves it. A loaded statement moves nothing (IP-134 as amended):
 * reconciliation is the stage after repricing, and the statements are its
 * checklist's.
 *
 * @param {string} current the day's stage
 * @param {{ nowMs, cutoffAt, books: { open }, fundsMissingPrice: string[] }} facts
 * @returns {{ stage: string, reason: string, skipped: string[] }|null}
 */
export function decideStage(current, facts) {
  if (rankOf(current) >= rankOf(STAGE.PRICED)) return null;
  let target = STAGE.OPEN;
  if (isFrozen(facts)) {
    target = STAGE.FROZEN;
    if ((facts.fundsMissingPrice ?? []).length === 0) target = STAGE.PRICED;
  }
  if (rankOf(target) <= rankOf(current)) return null;
  return { stage: target, reason: MOVE_REASON[target], skipped: stagesBetween(current, target) };
}

/**
 * The force to PRICED (design 5.5): a day whose books are closed (FROZEN's
 * facts) but which some fund still needs a price for moves to PRICED, the
 * funds named. null when there is nothing to force: the books are not
 * closed (never forced), every price is in (decideStage moves it), or the day
 * is already PRICED or beyond.
 *
 * @returns {{ stage: 'PRICED', reason: string, skipped: string[], forced: { stage: 'PRICED', missing: { fundsMissingPrice: string[] } } }|null}
 */
export function forcePriced(current, facts) {
  if (rankOf(current) >= rankOf(STAGE.PRICED) || !isFrozen(facts)) return null;
  const missing = [...(facts.fundsMissingPrice ?? [])];
  if (!missing.length) return null;
  return {
    stage: STAGE.PRICED,
    reason: MOVE_REASON.PRICED_FORCED,
    skipped: stagesBetween(current, STAGE.PRICED),
    forced: { stage: STAGE.PRICED, missing: { fundsMissingPrice: missing } },
  };
}

/**
 * Stage 6 (design 5.2, 5.5): whether a RECONCILING day may enter DAY_END.
 *
 *   pending orders     never passed: blockedBy PENDING_ORDERS, whatever force says
 *   funding window     open until fundingWindowClosesAt (none known: open)
 *   statements         a LOADED statement for every ACTIVE COLLECTION account
 *
 * The last two are what force passes: the move is then forced, naming what
 * was missing.
 *
 * @param {string} current the day's stage
 * @param {{ nowMs: number, fundingWindowClosesAt: string|null, statements: { missing: string[] }, pendingOrders: number }} facts
 * @param {{ force?: boolean }} [options]
 * @returns {{ move: boolean, stage?: 'DAY_END', reason?: string, blockedBy: string[],
 *   missing: { fundingWindowClosesAt: string|null, statementsMissing: string[], pendingOrders: number },
 *   forced?: { stage: 'DAY_END', missing: object } }|null} null unless the day is RECONCILING
 */
export function decideDayEnd(current, facts, { force = false } = {}) {
  if (current !== STAGE.RECONCILING) return null;
  const nowMs = msOf(facts.nowMs, 'nowMs');
  const closesAt = facts.fundingWindowClosesAt ?? null;
  const windowOpen = closesAt === null || nowMs < msOf(closesAt, 'fundingWindowClosesAt');
  const statementsMissing = [...(facts.statements?.missing ?? [])];
  const pendingOrders = Number(facts.pendingOrders ?? 0);
  const missing = { fundingWindowClosesAt: closesAt, statementsMissing, pendingOrders };
  const blockedBy = [
    ...(pendingOrders > 0 ? [BLOCKER.PENDING_ORDERS] : []),
    ...(windowOpen ? [BLOCKER.FUNDING_WINDOW_OPEN] : []),
    ...(statementsMissing.length ? [BLOCKER.STATEMENTS_MISSING] : []),
  ];
  if (pendingOrders > 0) return { move: false, blockedBy, missing };
  if (!blockedBy.length) return { move: true, stage: STAGE.DAY_END, reason: MOVE_REASON.DAY_END, blockedBy, missing };
  if (!force) return { move: false, blockedBy, missing };
  return {
    move: true,
    stage: STAGE.DAY_END,
    reason: MOVE_REASON.DAY_END_FORCED,
    blockedBy,
    missing,
    forced: {
      stage: STAGE.DAY_END,
      missing: { ...(windowOpen ? { fundingWindowClosesAt: closesAt } : {}), ...(statementsMissing.length ? { statementsMissing } : {}) },
    },
  };
}

/**
 * Whether a day's running step still pauses the maker's review (design 2.6):
 * REPRICING within 15 minutes of entering it, DAY_END within 5. A step older
 * than that is stale - it pauses nothing.
 *
 * @param {{ stage: string, stageEnteredAt: string }} day
 * @param {number} nowMs
 * @returns {'PAUSING'|'STALE'|null} null for a stage that is no step
 */
export function stepPause(day, nowMs) {
  const limit = PAUSE_LIMIT_MS[day?.stage];
  if (limit === undefined) return null;
  return msOf(nowMs, 'nowMs') - msOf(day.stageEnteredAt, 'stageEnteredAt') < limit ? 'PAUSING' : 'STALE';
}
